/**
 * On-screen readout: where the player is in the real world, and what the
 * streamer is doing.
 *
 * A single summary line is always shown; the rest folds away behind the ⓘ
 * button or the I key, in three tabs: *world* — where, when, sky and weather,
 * and the switches to set the weather and the season by hand —
 * *performance* — frame rate and times, resolution, the graphics card,
 * streaming, and the switches that trade looks for speed — and *sound*, its
 * volumes. Which is open, and the tab, are remembered. The mute button beside
 * ⓘ is always there.
 *
 * The attribution lines are not optional decoration — the data sources require
 * credit. OpenStreetMap's must be on the view itself, so it stays on screen
 * from the first frame in a short line in the bottom-left corner; the rest may
 * be one click away, behind the "Credits" button beside it, in a small panel
 * that follows the data under the player as it changes.
 */

import type { Graphics } from '../config.js';
import type { Weather } from '../core/weather/weather.js';
import type { WorldStats } from '../core/world.js';
import type { GraphicsChoice, GraphicsSetting } from './graphicsSettings.js';
import { isChosen } from './graphicsSettings.js';
import type { PlayerState } from './PlayerController.js';
import type { RenderScale, ScaleSetting } from './renderScale.js';
import { SCALE_CHOICES } from './renderScale.js';

/** Vehicles read in km/h; people in m/s. */
function speedText(player: PlayerState): string {
  if (player.mode === 'drive' || player.mode === 'moto' || player.mode === 'heli') {
    return `${(player.speed * 3.6).toFixed(0)} km/h`;
  }
  return `${player.speed.toFixed(0)} m/s`;
}

/** Rate of climb, shown only when it means something. */
function climbText(player: PlayerState, verticalSpeed: number): string {
  if (player.mode !== 'heli') return '';
  const sign = verticalSpeed >= 0 ? '+' : '';
  return `   vs ${sign}${verticalSpeed.toFixed(1)} m/s`;
}

const DETAILS_KEY = 'wsg.hud.details';
const GRAPHICS_KEY = 'wsg.hud.graphics';
const TAB_KEY = 'wsg.hud.tab';

type Tab = 'world' | 'performance' | 'sound';
const TABS: Tab[] = ['world', 'performance', 'sound'];

/** The sound's controls, as the HUD needs them: see `audio/system.ts`. */
export interface SoundControls {
  settings: { muted: boolean } & Record<'master' | 'engine' | 'world' | 'effects' | 'radio', number>;
  setMuted(muted: boolean): void;
  setLevel(level: 'master' | 'engine' | 'world' | 'effects' | 'radio', value: number): void;
}
const VOLUME_STEPS = [0, 0.25, 0.5, 0.75, 1];

/** The weather's switches, as the HUD needs them: see `main.ts`. */
export interface WeatherControls {
  simulating(): boolean;
  setSimulating(on: boolean): void;
  /** The weather in force: live, or simulated. */
  weather(): Weather;
  /** Set by hand: simulation on, `patch` over what is in force. */
  set(patch: Partial<Weather>): void;
  presets: readonly string[];
  preset(name: string): void;
  /** The season chosen by hand, undefined going by the date. */
  season(): string | undefined;
  seasons: readonly string[];
  setSeason(name: string | undefined): void;
}

/** The weather's sliders: what each sets, its range and step, and how its value reads. */
const WEATHER_SLIDERS: ReadonlyArray<{ key: keyof Weather; label: string; min: number; max: number; step: number; show: (v: number) => string }> = [
  { key: 'temperature', label: 'temperature', min: -30, max: 40, step: 1, show: v => `${v.toFixed(0)} °C` },
  { key: 'cloud', label: 'cloud', min: 0, max: 1, step: 0.05, show: v => `${Math.round(v * 100)}%` },
  { key: 'rain', label: 'rain', min: 0, max: 25, step: 0.5, show: v => `${v.toFixed(1)} mm/h` },
  { key: 'snowfall', label: 'snowfall', min: 0, max: 8, step: 0.25, show: v => `${v.toFixed(1)} cm/h` },
  { key: 'snowDepth', label: 'snow on ground', min: 0, max: 1.5, step: 0.02, show: v => `${Math.round(v * 100)} cm` },
  { key: 'fog', label: 'fog', min: 0, max: 1, step: 0.05, show: v => `${Math.round(v * 100)}%` },
  { key: 'wind', label: 'wind', min: 0, max: 35, step: 0.5, show: v => `${v.toFixed(1)} m/s` },
  { key: 'gusts', label: 'gusts', min: 0, max: 50, step: 0.5, show: v => `${v.toFixed(1)} m/s` },
  { key: 'windFrom', label: 'wind from', min: 0, max: 355, step: 5, show: v => `${v.toFixed(0)}°` },
];

const CSS = `
.wsg-hud { position: fixed; top: 0; left: 0; z-index: 10; max-width: calc(100vw - 24px);
  padding: 8px 10px 8px 12px; padding-top: max(8px, env(safe-area-inset-top));
  padding-left: max(12px, env(safe-area-inset-left));
  font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #d8e2ea;
  background: rgba(8, 12, 18, 0.62); border-bottom-right-radius: 6px; pointer-events: none; }
.wsg-hud .summary { display: flex; align-items: center; gap: 10px; }
.wsg-hud pre { min-width: 0; margin: 0; font: inherit; white-space: pre; overflow: hidden; text-overflow: ellipsis; }
.wsg-hud .details { margin-top: 6px; }
.wsg-hud .tabs { padding-bottom: 6px; border-bottom: 1px solid rgba(216, 226, 234, 0.18); }
.wsg-hud .setting { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.wsg-hud .setting > span { min-width: 10em; white-space: nowrap; }
.wsg-hud .setting input[type=range] { pointer-events: auto; width: 150px; margin: 0; accent-color: #2f6fb0; }
.wsg-hud .setting output { min-width: 5.5em; }
.wsg-hud .weather { margin-bottom: 4px; padding-bottom: 4px; border-bottom: 1px solid rgba(216, 226, 234, 0.18); }
.wsg-hud .setting button { width: auto; height: 22px; padding: 0 9px; font: 600 11px system-ui, sans-serif; }
.wsg-hud button { pointer-events: auto; flex: none; width: 24px; height: 24px; padding: 0; border: 0;
  border-radius: 12px; background: rgba(216, 226, 234, 0.16); color: inherit; font: 700 13px system-ui, sans-serif;
  cursor: pointer; }
.wsg-hud button.on { background: #2f6fb0; color: #fff; }
.wsg-credit { position: fixed; left: 0; bottom: 0; z-index: 10; box-sizing: border-box;
  max-width: calc(100vw - 256px); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  padding: 2px 8px; padding-bottom: max(2px, env(safe-area-inset-bottom));
  padding-left: max(8px, env(safe-area-inset-left));
  font: 500 10px system-ui, sans-serif; color: rgba(216, 226, 234, 0.85); background: rgba(8, 12, 18, 0.4);
  border-top-right-radius: 6px; pointer-events: none; }
.wsg-credit button { pointer-events: auto; padding: 0; border: 0; background: none; color: inherit;
  font: inherit; text-decoration: underline; cursor: pointer; }
.wsg-credit button[aria-expanded=true] { color: #fff; }
.wsg-credits { position: fixed; left: max(8px, env(safe-area-inset-left));
  bottom: max(26px, calc(env(safe-area-inset-bottom) + 20px)); z-index: 13; box-sizing: border-box;
  max-width: min(440px, calc(100vw - 16px)); max-height: 60vh; overflow: auto; padding: 8px 30px 8px 12px;
  font: 500 11px/1.5 system-ui, sans-serif; color: #d8e2ea; background: rgba(8, 12, 18, 0.85); border-radius: 6px; }
.wsg-credits h2 { margin: 0 0 4px; font: 700 11px system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.05em; }
.wsg-credits ul { margin: 0; padding: 0; list-style: none; }
.wsg-credits .close { position: absolute; top: 5px; right: 5px; width: 20px; height: 20px; padding: 0; border: 0;
  border-radius: 10px; background: rgba(216, 226, 234, 0.16); color: inherit; font: 700 12px system-ui, sans-serif; cursor: pointer; }
/* On touch screens the minimap sits top right, out of the corner's way. */
html.wsg-touch .wsg-credit { max-width: 100vw; }
@media (max-width: 640px) {
  .wsg-hud { font-size: 10.5px; }
  .wsg-credit { font-size: 9px; }
}
`;

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function readTab(): Tab {
  try {
    const tab = localStorage.getItem(TAB_KEY);
    return tab === 'performance' || tab === 'sound' ? tab : 'world';
  } catch {
    return 'world';
  }
}

function writeTab(tab: Tab): void {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // Not remembered; the tab still switches.
  }
}

function writeFlag(key: string, open: boolean): void {
  try {
    localStorage.setItem(key, open ? '1' : '0');
  } catch {
    // Private windows and blocked storage: the toggle still works, it is just not remembered.
  }
}

export class DebugHud {
  private readonly root: HTMLDivElement;
  private readonly summary: HTMLPreElement;
  private readonly details: HTMLDivElement;
  private readonly panes: Record<Tab, HTMLDivElement>;
  private readonly tabButtons = new Map<Tab, HTMLButtonElement>();
  private tab = readTab();
  private readonly worldReadout: HTMLPreElement;
  private readonly perfReadout: HTMLPreElement;
  private readonly scaleButtons = new Map<ScaleSetting, HTMLButtonElement>();
  private scale: RenderScale | undefined;
  /** Each graphics button with the patch it applies, lit while the graphics match it. */
  private readonly graphicsButtons: { button: HTMLButtonElement; patch: Partial<Graphics> }[] = [];
  private graphics: (() => Graphics) | undefined;
  private readonly settings: HTMLDivElement;
  private readonly toggleButton: HTMLButtonElement;
  private readonly credit: HTMLDivElement;
  /** The full list of credits, opened from the footer's button. */
  private readonly creditsPanel: HTMLDivElement;
  private readonly creditsList: HTMLUListElement;
  private readonly creditsButton: HTMLButtonElement;
  private creditsKey = '';
  private creditsOpen = false;
  private detailsOpen = readFlag(DETAILS_KEY);
  private frames = 0;
  private fps = 0;
  private lastSample = performance.now();
  /**
   * The longest frame in the last few seconds, ms. The frame rate is an
   * average and hides a freeze; this is the number a stutter shows up in.
   */
  private worst = 0;
  private worstInWindow = 0;
  private lastFrame = performance.now();
  private worstWindowStart = performance.now();

  constructor(parent: HTMLElement, attributions: string[]) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    this.root = document.createElement('div');
    this.root.className = 'wsg-hud';

    const top = document.createElement('div');
    top.className = 'summary';
    this.summary = document.createElement('pre');
    this.toggleButton = document.createElement('button');
    this.toggleButton.textContent = 'i';
    this.toggleButton.title = 'Details (I)';
    this.toggleButton.addEventListener('click', () => this.toggle());
    top.append(this.toggleButton, this.summary);
    this.root.appendChild(top);

    this.details = document.createElement('div');
    this.details.className = 'details';
    const tabs = document.createElement('div');
    tabs.className = 'setting tabs';
    for (const tab of TABS) {
      const button = document.createElement('button');
      button.textContent = tab;
      button.addEventListener('click', () => this.showTab(tab));
      this.tabButtons.set(tab, button);
      tabs.appendChild(button);
    }
    this.panes = { world: document.createElement('div'), performance: document.createElement('div'), sound: document.createElement('div') };
    this.worldReadout = document.createElement('pre');
    this.perfReadout = document.createElement('pre');
    this.settings = document.createElement('div');
    this.panes.world.append(this.worldReadout);
    this.panes.performance.append(this.settings, this.perfReadout);
    this.details.append(tabs, this.panes.world, this.panes.performance, this.panes.sound);
    this.root.appendChild(this.details);
    parent.appendChild(this.root);

    // OpenStreetMap's credit stays on the view; the rest is one click away.
    this.credit = document.createElement('div');
    this.credit.className = 'wsg-credit';
    this.creditsButton = document.createElement('button');
    this.creditsButton.type = 'button';
    this.creditsButton.textContent = 'Credits';
    this.creditsButton.setAttribute('aria-controls', 'wsg-credits');
    this.creditsButton.addEventListener('click', (event) => {
      this.showCredits(!this.creditsOpen);
      // A mouse click leaves the keys to the game; a key press keeps the focus here.
      if (event.detail > 0) this.creditsButton.blur();
    });
    this.credit.append(`${attributions[0] ?? ''}  ·  `, this.creditsButton);
    parent.appendChild(this.credit);

    this.creditsPanel = document.createElement('div');
    this.creditsPanel.className = 'wsg-credits';
    this.creditsPanel.id = 'wsg-credits';
    this.creditsPanel.setAttribute('role', 'region');
    this.creditsPanel.setAttribute('aria-label', 'Credits');
    const heading = document.createElement('h2');
    heading.textContent = 'Credits';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'close';
    close.textContent = '×';
    close.title = 'Close (Esc)';
    close.setAttribute('aria-label', 'Close credits');
    close.addEventListener('click', () => this.showCredits(false, true));
    this.creditsList = document.createElement('ul');
    this.creditsPanel.append(close, heading, this.creditsList);
    parent.appendChild(this.creditsPanel);
    this.showCredits(false);
    this.setCredits(attributions);

    window.addEventListener('keydown', (event) => {
      if (event.code === 'KeyI') this.toggle();
      if (event.code === 'Escape' && this.creditsOpen) this.showCredits(false, true);
    });
    this.apply();
  }

  /**
   * The full list of credits, in the panel behind the footer's button: it
   * changes with the data on screen, as the elevation source's overlay does,
   * whether the panel is open or not.
   */
  setCredits(attributions: readonly string[]): void {
    const key = attributions.join('\n');
    if (key === this.creditsKey) return;
    this.creditsKey = key;
    this.creditsList.replaceChildren(...attributions.map((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      return item;
    }));
  }

  /** Open or close the credits; closing by keyboard hands the focus back to the footer's button if it was inside. */
  private showCredits(open: boolean, refocus = false): void {
    if (!open && refocus && this.creditsPanel.contains(document.activeElement)) this.creditsButton.focus();
    this.creditsOpen = open;
    this.creditsPanel.hidden = !open;
    this.creditsButton.setAttribute('aria-expanded', String(open));
  }

  /** Show the resolution choices at the top of the details. */
  setRenderScale(scale: RenderScale): void {
    this.scale = scale;
    const row = document.createElement('div');
    row.className = 'setting';
    const label = document.createElement('span');
    label.textContent = 'resolution';
    row.appendChild(label);
    for (const choice of SCALE_CHOICES) {
      const button = document.createElement('button');
      button.addEventListener('click', () => {
        scale.choose(choice);
        this.refreshScale();
      });
      this.scaleButtons.set(choice, button);
      row.appendChild(button);
    }
    this.panes.performance.prepend(row);
    this.refreshScale();
  }

  /**
   * The graphics switches, folded behind a button of their own under the
   * resolution: `current` is the graphics in use, `choose` applies a patch
   * over them, and `reset` goes back to the profile's.
   */
  setGraphics(
    settings: readonly GraphicsSetting[],
    presets: readonly GraphicsChoice[],
    current: () => Graphics,
    choose: (patch: Partial<Graphics>) => void,
    reset: () => void,
  ): void {
    this.graphics = current;
    const panel = document.createElement('div');
    const toggle = document.createElement('button');
    toggle.textContent = 'graphics';
    let open = readFlag(GRAPHICS_KEY);
    const show = () => {
      panel.style.display = open ? '' : 'none';
      toggle.classList.toggle('on', open);
    };
    toggle.addEventListener('click', () => {
      open = !open;
      writeFlag(GRAPHICS_KEY, open);
      show();
    });
    const head = document.createElement('div');
    head.className = 'setting';
    head.appendChild(toggle);
    const resetButton = document.createElement('button');
    resetButton.textContent = 'reset';
    resetButton.title = "Back to this device's own settings";
    resetButton.addEventListener('click', reset);
    head.appendChild(resetButton);

    const row = (label: string, choices: readonly GraphicsChoice[]) => {
      const el = document.createElement('div');
      el.className = 'setting';
      const name = document.createElement('span');
      name.textContent = label;
      el.appendChild(name);
      for (const choice of choices) {
        const button = document.createElement('button');
        button.textContent = choice.label;
        button.addEventListener('click', () => {
          choose(choice.patch);
          this.refreshGraphics();
        });
        this.graphicsButtons.push({ button, patch: choice.patch });
        el.appendChild(button);
      }
      panel.appendChild(el);
    };
    row('preset', presets);
    for (const setting of settings) row(setting.label, setting.choices);
    this.settings.append(head, panel);
    show();
    this.refreshGraphics();
  }

  /** The mute button beside ⓘ, and the sound tab's volumes. */
  setSound(sound: SoundControls): void {
    const lit: Array<{ button: HTMLButtonElement; on: () => boolean }> = [];
    const refresh = () => {
      for (const { button, on } of lit) button.classList.toggle('on', on());
      mute.textContent = sound.settings.muted ? '🔇' : '🔊';
      mute.title = sound.settings.muted ? 'Sound off: click for sound' : 'Sound on: click to mute';
    };
    const button = (text: string, on: () => boolean, click: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.addEventListener('click', () => {
        click();
        refresh();
      });
      lit.push({ button: b, on });
      return b;
    };
    const mute = button('', () => !sound.settings.muted, () => sound.setMuted(!sound.settings.muted));
    this.toggleButton.after(mute);

    const row = (label: string, buttons: HTMLButtonElement[]) => {
      const el = document.createElement('div');
      el.className = 'setting';
      const name = document.createElement('span');
      name.textContent = label;
      el.append(name, ...buttons);
      this.panes.sound.appendChild(el);
    };
    row('sound', [
      button('on', () => !sound.settings.muted, () => sound.setMuted(false)),
      button('muted', () => sound.settings.muted, () => sound.setMuted(true)),
    ]);
    for (const level of ['master', 'engine', 'world', 'effects', 'radio'] as const) {
      row(level === 'master' ? 'volume' : level === 'radio' ? 'car radio' : level, VOLUME_STEPS.map(value =>
        button(`${value * 100}%`, () => Math.abs(sound.settings[level] - value) < 0.01, () => sound.setLevel(level, value))));
    }
    this.soundReadout = document.createElement('pre');
    this.panes.sound.appendChild(this.soundReadout);
    refresh();
  }

  private soundReadout: HTMLPreElement | undefined;
  /** Each weather control's refresh: its light, or its slider's value. */
  private readonly weatherRefresh: Array<() => void> = [];

  /**
   * The world tab's weather and season switches: live or simulated, a
   * preset, sliders for each part of the weather, thunder, and the season.
   * The sliders are shown while simulating.
   */
  setWeather(c: WeatherControls): void {
    const box = document.createElement('div');
    box.className = 'weather';
    const row = (label: string, ...items: HTMLElement[]) => {
      const el = document.createElement('div');
      el.className = 'setting';
      const name = document.createElement('span');
      name.textContent = label;
      el.append(name, ...items);
      return el;
    };
    const button = (text: string, on: () => boolean, click: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.addEventListener('click', () => {
        click();
        this.refreshWeather();
      });
      this.weatherRefresh.push(() => b.classList.toggle('on', on()));
      return b;
    };
    box.append(
      row('weather', button('live', () => !c.simulating(), () => c.setSimulating(false)), button('simulate', () => c.simulating(), () => c.setSimulating(true))),
      row('preset', ...c.presets.map(name => button(name, () => false, () => c.preset(name)))),
    );
    const sliders = document.createElement('div');
    for (const def of WEATHER_SLIDERS) {
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(def.min);
      input.max = String(def.max);
      input.step = String(def.step);
      const value = document.createElement('output');
      input.addEventListener('input', () => {
        c.set({ [def.key]: Number(input.value) });
        value.textContent = def.show(Number(input.value));
      });
      // Let go of the slider, and the keys are the game's again.
      input.addEventListener('change', () => input.blur());
      this.weatherRefresh.push(() => {
        const v = c.weather()[def.key] as number;
        if (document.activeElement !== input) input.value = String(v);
        value.textContent = def.show(v);
      });
      sliders.append(row(def.label, input, value));
    }
    sliders.append(row('thunder', button('off', () => !c.weather().thunder, () => c.set({ thunder: false })), button('on', () => c.weather().thunder, () => c.set({ thunder: true }))));
    this.weatherRefresh.push(() => { sliders.style.display = c.simulating() ? '' : 'none'; });
    box.append(sliders, row('season ↻', button('live', () => c.season() === undefined, () => c.setSeason(undefined)),
      ...c.seasons.map(name => button(name, () => c.season() === name, () => c.setSeason(name)))));
    this.panes.world.prepend(box);
    this.refreshWeather();
  }

  private refreshWeather(): void {
    for (const refresh of this.weatherRefresh) refresh();
  }

  /** The sound tab's readout: what is playing. */
  setSoundText(lines: string[]): void {
    if (this.soundReadout !== undefined && this.detailsOpen && this.tab === 'sound') this.soundReadout.textContent = lines.join('\n');
  }

  private refreshGraphics(): void {
    if (this.graphics === undefined) return;
    const graphics = this.graphics();
    for (const { button, patch } of this.graphicsButtons) button.classList.toggle('on', isChosen(graphics, patch));
  }

  private refreshScale(): void {
    const scale = this.scale;
    if (scale === undefined) return;
    for (const [choice, button] of this.scaleButtons) {
      button.textContent = choice === 'auto'
        ? `auto${scale.setting === 'auto' ? ` ${Math.round(scale.scale * 100)}%` : ''}`
        : `${choice * 100}%`;
      button.classList.toggle('on', choice === scale.setting);
    }
  }

  toggle(): void {
    this.detailsOpen = !this.detailsOpen;
    writeFlag(DETAILS_KEY, this.detailsOpen);
    this.apply();
  }

  private showTab(tab: Tab): void {
    this.tab = tab;
    writeTab(tab);
    this.apply();
  }

  private apply(): void {
    this.details.style.display = this.detailsOpen ? '' : 'none';
    this.toggleButton.classList.toggle('on', this.detailsOpen);
    for (const tab of TABS) {
      this.panes[tab].style.display = tab === this.tab ? '' : 'none';
      this.tabButtons.get(tab)?.classList.toggle('on', tab === this.tab);
    }
  }

  tick(): void {
    this.frames++;
    const now = performance.now();
    const frame = now - this.lastFrame;
    this.lastFrame = now;
    // A hidden tab's gap is not a frame.
    if (document.visibilityState === 'visible' && frame < 5000) this.worstInWindow = Math.max(this.worstInWindow, frame);
    if (now - this.worstWindowStart >= 3000) {
      this.worst = this.worstInWindow;
      this.worstInWindow = 0;
      this.worstWindowStart = now;
    }
    const elapsed = now - this.lastSample;
    if (elapsed >= 500) {
      this.fps = (this.frames * 1000) / elapsed;
      this.frames = 0;
      this.lastSample = now;
    }
  }

  update(
    player: PlayerState,
    stats: WorldStats,
    ground: number | undefined,
    verticalSpeed = 0,
    /** Time, sky and weather lines, for the world tab. */
    world: string[] = [],
    /** Frame times, resolution and the graphics card, for the performance tab. */
    perf: string[] = [],
  ): void {
    this.summary.textContent = `${player.mode}   ${speedText(player)}`;
    if (!this.detailsOpen) return;

    const agl = ground === undefined ? '—' : `${(player.altitude - ground).toFixed(0)} m`;
    const groundText = ground === undefined ? 'loading' : `${ground.toFixed(1)} m`;
    const mb = (stats.cacheBytes / (1024 * 1024)).toFixed(1);

    if (this.tab === 'world') this.refreshWeather();
    this.worldReadout.textContent = [
      `${player.lat.toFixed(6)}, ${player.lon.toFixed(6)}`,
      `alt ${player.altitude.toFixed(1)} m   agl ${agl}   ground ${groundText}`,
      `hdg ${player.heading.toFixed(0).padStart(3, '0')}${climbText(player, verticalSpeed)}`,
      ...world,
    ].join('\n');
    this.refreshScale();
    this.refreshGraphics();
    this.perfReadout.textContent = [
      `${this.fps.toFixed(0)} fps   worst ${this.worst.toFixed(0)} ms`,
      ...perf,
      '',
      `terrain ${stats.active}   loading ${stats.inFlight}   queued ${stats.pending}`,
      `vector  ${stats.vectorActive}   loading ${stats.vectorPending}   waiting ${stats.vectorDeferred}`,
      `cache ${stats.cached} tiles / ${mb} MB   failed ${stats.failed}   rebases ${stats.rebases}`,
    ].join('\n');
  }
}
